/**
 * Per-brand size guide URL configurations (Build C).
 *
 * Each entry maps a canonical retailer_id to a (gender, category) → URL binding.
 * URLs are POC-verified. Refresh via `fetch-brand-size-guide` when a retailer redesigns.
 */

export type BrandGuideEntry = {
  retailer_id: string;
  gender: "women" | "men" | "unisex";
  category: "tops" | "bottoms" | "dresses";
  url: string;
  table_hint?: {
    table_index?: number;
    keyword?: string;
  };
  notes?: string;
};

export const BRAND_GUIDE_CONFIGS: BrandGuideEntry[] = [
  {
    retailer_id: "hm",
    gender: "women",
    category: "tops",
    url: "https://www.hm.com/xk/customer-service/sizeguide/ladies/",
    notes:
      "Kosovo regional path — UK and other EU paths return SPA shell. POC verified Kosovo path returns static HTML tables.",
  },
  {
    retailer_id: "john_lewis",
    gender: "women",
    category: "tops",
    url: "https://www.johnlewis.com/buying-guides/intenational-size-conversion",
    notes:
      "Note the typo'd 'intenational' in URL — that IS the canonical path per POC. JL hides charts under buying-guides hub.",
    table_hint: { keyword: "Women" },
  },
  {
    retailer_id: "ms",
    gender: "women",
    category: "tops",
    url: "https://www.marksandspencer.com/us/sizeguides/?cid=sizeguidewomens-tops",
    notes:
      "US sub-path required — UK /c/size-guides is SPA-rendered. cid query param scopes to women's tops.",
  },
  {
    retailer_id: "next",
    gender: "women",
    category: "tops",
    url: "https://zendesk.next.co.uk/hc/en-gb/articles/360021245914-Women-s-Sizing-Guides",
    notes:
      "Zendesk help-centre — fully static. 23 tables total. Use table_hint to identify tops.",
    table_hint: { keyword: "tops" },
  },
  {
    retailer_id: "river_island",
    gender: "women",
    category: "tops",
    url: "https://www.riverisland.com/how-can-we-help/size-guides/womens",
    notes:
      "Clean static page, 21 tables across categories. Use table_hint to identify tops.",
    table_hint: { keyword: "tops" },
  },
];

/**
 * Retailers known to be incompatible with brand-level static scraping.
 * SPA-architecture sites where size charts load via XHR after initial render.
 * Engine should skip brand-level fetch and per-product Firecrawl for these.
 */
export const SPA_INCOMPATIBLE_RETAILERS = new Set<string>([
  "zara",
  "bershka",
  "pull_and_bear",
  "massimo_dutti",
  "stradivarius",
]);

export function getBrandGuideConfig(
  retailer_id: string,
  gender: "women" | "men" | "unisex",
  category: "tops" | "bottoms" | "dresses",
): BrandGuideEntry | null {
  return (
    BRAND_GUIDE_CONFIGS.find(
      (e) =>
        e.retailer_id === retailer_id &&
        e.gender === gender &&
        e.category === category,
    ) ?? null
  );
}

export function isSpaIncompatible(retailer_id: string | null | undefined): boolean {
  if (!retailer_id) return false;
  return SPA_INCOMPATIBLE_RETAILERS.has(retailer_id);
}
